import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, assetsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  QrCode, ClipboardList, Play, CheckCircle, AlertTriangle,
  Clock, Package, Search, Camera, X, ChevronRight, Smartphone,
  MapPin, Calendar, Zap
} from 'lucide-react';
import { format, parseISO, isAfter } from 'date-fns';

const PRIORITY_COLORS = { critical:'#EF4444', high:'#F97316', medium:'#F59E0B', low:'#10B981' };
const STATUS_BG = { pending:'#FEF3C7', assigned:'#EDE9FE', in_progress:'#E0F2FE', completed:'#D1FAE5' };
const STATUS_TEXT = { pending:'#92400E', assigned:'#5B21B6', in_progress:'#0C4A6E', completed:'#065F46' };

import MobileJobCard from '../components/mobile/MobileJobCard';
import QRScannerModal from '../components/mobile/QRScannerModal';

function AssetJobPicker({ data, onClose, onSelectJob }) {
  const { asset, active_jobs } = data;
  return (
    <div className="modal-overlay">
      <div className="modal modal-md">
        <div className="modal-header">
          <div>
            <div className="modal-title">{asset.name}</div>
            <div style={{ fontFamily:'var(--font-mono)', fontSize:12, color:'var(--accent)', marginTop:2 }}>{asset.asset_code}</div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div style={{ padding:'12px 16px', background:'var(--bg-muted)', borderRadius:'var(--r-md)', border:'1px solid var(--border)', display:'flex', gap:16 }}>
            <div>
              <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase' }}>Location</div>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{asset.location || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase' }}>Category</div>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>{asset.category_name || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize:10, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase' }}>Status</div>
              <span className={`badge badge-${asset.status}`}>{asset.status?.replace('_',' ')}</span>
            </div>
          </div>

          {!active_jobs?.length ? (
            <div className="empty-state" style={{ padding:'32px 0' }}>
              <CheckCircle size={32} color="#10B981" />
              <div className="empty-title">No active jobs</div>
              <div className="empty-sub">This asset has no pending or assigned jobs right now</div>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5 }}>
                {active_jobs.length} Active Job{active_jobs.length>1?'s':''}
              </div>
              {active_jobs.map(j => (
                <button key={j.id} onClick={() => onSelectJob(j.id)} style={{
                  background:'var(--bg-muted)', border:'1px solid var(--border)', borderRadius:'var(--r-md)',
                  padding:'12px 16px', cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center',
                  gap:12, transition:'all 150ms'
                }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:'var(--font-mono)', fontSize:11, color:'var(--accent)' }}>{j.job_number}</div>
                    <div style={{ fontSize:14, fontWeight:700, color:'var(--text)' }}>{j.title}</div>
                    <div style={{ display:'flex', gap:8, marginTop:4 }}>
                      <span className={`badge badge-${j.status}`}>{j.status?.replace('_',' ')}</span>
                      <span className={`badge badge-${j.priority}`}>{j.priority}</span>
                    </div>
                  </div>
                  <ChevronRight size={16} color="var(--text-muted)" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TechnicianPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myJobs, setMyJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('my_jobs'); // 'my_jobs' | 'scan'
  const [showScanner, setShowScanner] = useState(false);
  const [assetData, setAssetData] = useState(null);

  useEffect(() => {
    jobsAPI.myJobs().then(r => setMyJobs(r.data.jobs)).finally(() => setLoading(false));
  }, []);

  const overdue = myJobs.filter(j => j.due_date && isAfter(new Date(), new Date(j.due_date)));
  const inProgress = myJobs.filter(j => j.status === 'in_progress');
  const assigned = myJobs.filter(j => j.status === 'assigned');

  return (
    <div style={{ maxWidth:640, margin:'0 auto', display:'flex', flexDirection:'column', gap:16 }}>
      {/* Mobile header card */}
      <div style={{
        background:'linear-gradient(135deg, #0F172A, #1E293B)',
        borderRadius:'var(--r-xl)', padding:'24px',
        display:'flex', flexDirection:'column', gap:16
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:44, height:44, borderRadius:'var(--r-md)', background:'linear-gradient(135deg, #0EA5E9, #0284C7)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Smartphone size={22} color="#fff" />
          </div>
          <div>
            <div style={{ color:'#fff', fontSize:17, fontWeight:800 }}>Field View</div>
            <div style={{ color:'#64748B', fontSize:12 }}>Hello, {user?.name?.split(' ')[0]}</div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 }}>
          {[
            { v:myJobs.length, l:'Total', c:'#0EA5E9' },
            { v:inProgress.length, l:'In Progress', c:'#F59E0B' },
            { v:overdue.length, l:'Overdue', c:'#EF4444' },
          ].map(s => (
            <div key={s.l} style={{ textAlign:'center', background:'rgba(255,255,255,0.06)', borderRadius:'var(--r-md)', padding:'12px 8px' }}>
              <div style={{ fontSize:24, fontWeight:900, color:s.c }}>{s.v}</div>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', fontWeight:600 }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* QR Scan button */}
        <button className="btn" style={{
          background:'rgba(14,165,233,0.15)', border:'1.5px solid rgba(14,165,233,0.3)',
          color:'#0EA5E9', width:'100%', justifyContent:'center', fontSize:14
        }} onClick={() => setShowScanner(true)}>
          <QrCode size={18} />
          Scan Asset QR Code
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, background:'var(--bg-muted)', borderRadius:'var(--r-md)', padding:4 }}>
        {[['my_jobs','My Jobs',myJobs.length],['all','All Active',null]].map(([key, label, count]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            flex:1, padding:'8px 16px', borderRadius:'var(--r-sm)', border:'none', cursor:'pointer',
            background: tab===key ? 'var(--bg-card)' : 'none',
            color: tab===key ? 'var(--text)' : 'var(--text-muted)',
            fontWeight:700, fontSize:13, boxShadow: tab===key ? 'var(--shadow-sm)' : 'none',
            display:'flex', alignItems:'center', justifyContent:'center', gap:6, transition:'all 150ms'
          }}>
            {label}
            {count !== null && <span style={{ background:tab===key?'var(--accent-muted)':undefined, color:tab===key?'var(--accent)':undefined, fontSize:11, fontWeight:700, padding:'1px 6px', borderRadius:10 }}>{count}</span>}
          </button>
        ))}
      </div>

      {/* Urgent jobs */}
      {overdue.length > 0 && (
        <div style={{ background:'#FFF1F2', border:'1.5px solid #FCA5A5', borderRadius:'var(--r-lg)', padding:'14px 16px', display:'flex', gap:10, alignItems:'flex-start' }}>
          <AlertTriangle size={18} color="#EF4444" style={{ flexShrink:0, marginTop:1 }} />
          <div>
            <div style={{ fontSize:14, fontWeight:800, color:'#991B1B' }}>{overdue.length} job{overdue.length>1?'s':''} overdue</div>
            <div style={{ fontSize:12, color:'#B91C1C', marginTop:2 }}>Please complete these immediately</div>
          </div>
        </div>
      )}

      {/* Jobs list */}
      {loading ? (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height:100, borderRadius:'var(--r-lg)' }} />)}
        </div>
      ) : myJobs.length === 0 ? (
        <div className="card"><div className="empty-state">
          <CheckCircle size={40} color="#10B981" />
          <div className="empty-title">All clear!</div>
          <div className="empty-sub">No active jobs assigned to you</div>
        </div></div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {/* In progress first */}
          {inProgress.length > 0 && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.8 }}>In Progress</div>
              {inProgress.map(j => <MobileJobCard key={j.id} job={j} onClick={() => navigate(`/jobs/${j.id}`)} />)}
            </>
          )}
          {/* Then assigned */}
          {assigned.length > 0 && (
            <>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.8, marginTop:4 }}>Assigned</div>
              {assigned.map(j => <MobileJobCard key={j.id} job={j} onClick={() => navigate(`/jobs/${j.id}`)} />)}
            </>
          )}
          {/* Other statuses */}
          {myJobs.filter(j => !['in_progress','assigned'].includes(j.status)).map(j => (
            <MobileJobCard key={j.id} job={j} onClick={() => navigate(`/jobs/${j.id}`)} />
          ))}
        </div>
      )}

      {showScanner && (
        <QRScannerModal
          onResult={(data) => { setShowScanner(false); setAssetData(data); }}
          onClose={() => setShowScanner(false)}
        />
      )}
      {assetData && (
        <AssetJobPicker
          data={assetData}
          onClose={() => setAssetData(null)}
          onSelectJob={(jobId) => { setAssetData(null); navigate(`/jobs/${jobId}`); }}
        />
      )}
    </div>
  );
}
